import {
  AddLinearIntegrationProjectRequestConfig,
  CreateLinearIntegrationConfigRequestConfig,
  DeleteLinearIntegrationConfigRequestConfig,
  LinearDiscoverRequestConfig,
  type LinearIntegrationConfig,
  LinearIntegrationConfigsRequestConfig,
  type LinearSyncMode,
  type LinearTeam,
  type LinearWebhookInfo,
  LinearWebhookRequestConfig,
  RemoveLinearIntegrationProjectRequestConfig,
  UpdateLinearCredentialsRequestConfig,
  UpdateLinearSyncModeRequestConfig,
  UpdateLinearWebhookSecretRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import { isServer } from "solid-js/web";
import { type FeatureAction, trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export type { LinearIntegrationConfig, LinearSyncMode, LinearTeam, LinearWebhookInfo } from "@storytell/client";

interface CreateLinearIntegrationInput {
  projectIds: string[];
  clientId: string;
  clientSecret: string;
}

class LinearIntegrationError extends Error {
  readonly errorResult: { code?: string | number; message?: string; statusCode?: number };

  constructor(payload: { code?: string | number; message?: string; statusCode?: number }) {
    const message = payload.message ?? "Request failed";
    super(message);
    this.name = "LinearIntegrationError";
    this.errorResult = {
      code: payload.code,
      message,
      statusCode: payload.statusCode,
    };
  }
}

const toError = (e: Error): LinearIntegrationError => {
  const meta = e as Error & { code?: string; status?: number };
  return new LinearIntegrationError({
    code: meta.code ?? meta.status,
    message: e.message,
    statusCode: meta.status,
  });
};

export function useLinear() {
  const wire = useWire();
  const client = useStorytellClient();

  const send = async <T>(
    doFetch: () => Promise<Result<T, Error>>,
    action?: FeatureAction<"integration.linear">
  ): Promise<Result<T, LinearIntegrationError>> => {
    if (isServer) return err(new LinearIntegrationError({ statusCode: 0 }));
    const res = (await doFetch()).mapErr(toError);
    if (action && res.isOk()) trackFeature({ feature: "integration.linear", action, via: "button" });
    return res;
  };

  const listConfigs = async (): Promise<Result<LinearIntegrationConfig[], LinearIntegrationError>> => {
    const res = await send(() =>
      client.fetch(LinearIntegrationConfigsRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );
    // Grants maps out-of-scope reads to 404, so non-admins get an empty list
    // instead of an error.
    if (res.isErr() && res.error.errorResult.statusCode === 404) return ok([]);
    return res;
  };

  const createConfig = (
    input: CreateLinearIntegrationInput
  ): Promise<Result<LinearIntegrationConfig, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          CreateLinearIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()),
          undefined,
          { projectIds: input.projectIds, clientId: input.clientId, clientSecret: input.clientSecret },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "connect"
    );

  const deleteConfig = (integrationId: string): Promise<Result<void, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          DeleteLinearIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "disconnect"
    );

  // The backend re-mints the app actor token before storing the credentials and
  // clears authBrokenAt.
  const updateCredentials = (
    integrationId: string,
    clientId: string,
    clientSecret: string
  ): Promise<Result<LinearIntegrationConfig, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateLinearCredentialsRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { clientId, clientSecret },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "reconnect"
    );

  // A new deployment starts with no team selection, so nothing syncs until the
  // settings panel picks one.
  const addProject = (
    integrationId: string,
    projectId: string
  ): Promise<Result<LinearIntegrationConfig, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          AddLinearIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { projectId },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "deploy"
    );

  const removeProject = (integrationId: string, projectId: string): Promise<Result<void, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          RemoveLinearIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "undeploy"
    );

  // allowPrune must be true for a narrowing change; the backend rejects an
  // unconfirmed narrowing with 409.
  const updateSyncMode = (
    integrationId: string,
    projectId: string,
    input: {
      syncMode: LinearSyncMode;
      selectedTeamIds: string[];
      teamProjects: Record<string, string[]>;
      includeUnprojected: boolean;
      allowPrune: boolean;
    }
  ): Promise<Result<LinearIntegrationConfig, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateLinearSyncModeRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          input,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  const discover = async (integrationId: string): Promise<Result<LinearTeam[], LinearIntegrationError>> => {
    const res = await send(() =>
      client.fetch(LinearDiscoverRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );
    return res.map((value) => value.teams);
  };

  const getWebhook = (integrationId: string): Promise<Result<LinearWebhookInfo, LinearIntegrationError>> =>
    send(() =>
      client.fetch(LinearWebhookRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );

  // Linear shows the signing secret once, so it arrives as a second paste.
  const saveWebhookSecret = (
    integrationId: string,
    signingSecret: string
  ): Promise<Result<LinearIntegrationConfig, LinearIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateLinearWebhookSecretRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { signingSecret },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  return {
    listConfigs,
    createConfig,
    deleteConfig,
    updateCredentials,
    addProject,
    removeProject,
    updateSyncMode,
    discover,
    getWebhook,
    saveWebhookSecret,
  };
}
